import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./DashboardHome.css";
import { admGetDashboardOverview } from "../../../services/adminAdminCoreService";

const API_HOST = process.env.REACT_APP_API_URL || "http://localhost:8080";
const absUrl = (value) => (value ? (String(value).startsWith("http") || String(value).startsWith("data:") ? String(value) : `${API_HOST}${value}`) : "");

const fmtMoney = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString("vi-VN") + "đ";
};

const shortText = (value, max = 120) => {
  const text = String(value || "").trim();
  if (!text) return "Combo đang mở bán và sẵn sàng cho owner gửi yêu cầu mua.";
  return text.length > max ? `${text.slice(0, max).trim()}...` : text;
};

export default function DashboardHome() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await admGetDashboardOverview({ days: 30 });
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "Không tải được dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const metrics = useMemo(() => {
    const c = data?.cards || {};
    return {
      franchisePending: Number(c.franchisePending || 0),
      comboPending: Number(c.comboPending || 0),
      maintenancePending: Number(c.maintenancePending || 0),
      activeGyms: Number(c.activeGyms || 0),
      comboRevenue30d: Number(c.comboRevenue30d || 0),
    };
  }, [data]);

  const cards = useMemo(() => {
    return [
      {
        label: "Nhượng quyền chờ duyệt",
        value: metrics.franchisePending,
        note: "Cần admin xử lý",
      },
      {
        label: "Yêu cầu mua combo chờ duyệt",
        value: metrics.comboPending,
        note: "Chờ xác nhận workflow",
      },
      {
        label: "Yêu cầu bảo trì chờ xử lý",
        value: metrics.maintenancePending,
        note: "Request kỹ thuật đang mở",
      },
      {
        label: "Phòng gym đang hoạt động",
        value: metrics.activeGyms,
        note: "Số chi nhánh đang hoạt động",
      },
    ];
  }, [metrics]);

  const revenueSeries = useMemo(() => data?.revenue30dSeries || [], [data]);
  const sellingCombos = useMemo(() => {
    const rows = Array.isArray(data?.sellingCombos) ? [...data.sellingCombos] : [];
    rows.sort((a, b) => {
      const soldDiff = Number(b?.soldCount || 0) - Number(a?.soldCount || 0);
      if (soldDiff !== 0) return soldDiff;
      return new Date(b?.updatedAt || 0).getTime() - new Date(a?.updatedAt || 0).getTime();
    });
    return rows.slice(0, 4);
  }, [data]);
  const comboSalesTransactions = useMemo(() => data?.comboSalesTransactions || [], [data]);
  const alerts = useMemo(() => data?.alerts || [], [data]);
  const assetKpis = useMemo(() => data?.assetKpis || {}, [data]);
  const lifecycle = useMemo(() => assetKpis?.byLifecycleStatus || {}, [assetKpis]);
  const maxRevenue = useMemo(() => Math.max(1, ...revenueSeries.map((x) => Number(x.total || 0))), [revenueSeries]);
  const todayRevenue = useMemo(() => Number(revenueSeries?.[revenueSeries.length - 1]?.total || 0), [revenueSeries]);
  const midRevenue = useMemo(() => Math.round(maxRevenue / 2), [maxRevenue]);
  const maxPointIndex = useMemo(() => {
    if (!revenueSeries.length) return -1;
    let idx = 0;
    let best = Number(revenueSeries[0]?.total || 0);
    for (let i = 1; i < revenueSeries.length; i += 1) {
      const value = Number(revenueSeries[i]?.total || 0);
      if (value > best) {
        best = value;
        idx = i;
      }
    }
    return idx;
  }, [revenueSeries]);
  const chartPoints = useMemo(() => {
    if (!revenueSeries.length) return "";
    const width = 720;
    const height = 220;
    const step = revenueSeries.length <= 1 ? width : width / (revenueSeries.length - 1);
    return revenueSeries
      .map((point, idx) => {
        const x = Number((idx * step).toFixed(2));
        const ratio = Number(point?.total || 0) / maxRevenue;
        const y = Number((height - ratio * 180 - 20).toFixed(2));
        return `${x},${y}`;
      })
      .join(" ");
  }, [revenueSeries, maxRevenue]);
  const chartAreaPoints = useMemo(() => {
    if (!chartPoints) return "";
    return `${chartPoints} 720,220 0,220`;
  }, [chartPoints]);
  const chartLabelPoints = useMemo(() => {
    if (!revenueSeries.length || maxPointIndex < 0) return [];
    const width = 720;
    const height = 220;
    const step = revenueSeries.length <= 1 ? width : width / (revenueSeries.length - 1);
    const pick = new Set([maxPointIndex, revenueSeries.length - 1]);
    return Array.from(pick)
      .filter((idx) => idx >= 0 && idx < revenueSeries.length)
      .map((idx) => {
        const value = Number(revenueSeries[idx]?.total || 0);
        const x = Number((idx * step).toFixed(2));
        const y = Number((height - (value / maxRevenue) * 180 - 20).toFixed(2));
        return { idx, x, y, value, date: revenueSeries[idx]?.date };
      });
  }, [revenueSeries, maxRevenue, maxPointIndex]);
  const firstDate = revenueSeries?.[0]?.date || "-";
  const middleDate = revenueSeries?.[Math.floor(revenueSeries.length / 2)]?.date || "-";
  const lastDate = revenueSeries?.[revenueSeries.length - 1]?.date || "-";

  return (
    <div className="dh-wrap dh-wrap--combo">
      <section className="dh-hero">
        <div className="dh-head">
          <div>
            <div className="dh-eyebrow">BẢNG ĐIỀU KHIỂN QUẢN TRỊ</div>
            <h2 className="dh-title">Tổng quan vận hành hệ thống</h2>
            <div className="dh-sub">Theo dõi nhanh nhượng quyền, bảo trì, combo và dòng tiền để xử lý các đầu việc quan trọng trong ngày.</div>
          </div>
          <div className="dh-actions">
            <button className="dh-btn" onClick={load} disabled={loading}>
              {loading ? "Đang tải..." : "Làm mới"}
            </button>
          </div>
        </div>

        <div className="dh-quick">
          <button className="dh-quick__item" type="button" onClick={() => navigate("/admin/franchises")}>
            <span>Nhượng quyền</span>
            <strong>{metrics.franchisePending} chờ duyệt</strong>
          </button>
          <button className="dh-quick__item" type="button" onClick={() => navigate("/admin/purchase-workflow")}>
            <span>Yêu cầu combo</span>
            <strong>{metrics.comboPending} chờ xử lý</strong>
          </button>
          <button className="dh-quick__item" type="button" onClick={() => navigate("/admin/maintenance")}>
            <span>Bảo trì</span>
            <strong>{metrics.maintenancePending} yêu cầu mở</strong>
          </button>
          <div className="dh-quick__item is-static">
            <span>Doanh thu hôm nay</span>
            <strong>{fmtMoney(todayRevenue)}</strong>
          </div>
        </div>
      </section>

      {err ? <div className="dh-alert">{err}</div> : null}

      <div className="dh-kpis">
        {cards.map((item) => (
          <div className="dh-kpi" key={item.label}>
            <div className="dh-kpi__label">{item.label}</div>
            <div className="dh-kpi__value">{item.value}</div>
            <div className="dh-kpi__note">{item.note}</div>
          </div>
        ))}
      </div>

      <div className="dh-main-grid">
        <section className="dh-panel dh-panel--alerts">
          <div className="dh-panel__head dh-panel__head--stack">
            <div className="dh-panel__title">Cảnh báo vận hành</div>
            <div className="dh-panel__hint">Theo dõi các điểm nghẽn: combo đã thanh toán chưa bàn giao, tài sản thiếu QR, bảo trì quá hạn</div>
          </div>

          {!alerts.length ? (
            <div className="dh-empty">Chưa có cảnh báo.</div>
          ) : (
            <div className="dh-alertList">
              {alerts.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  className={`dh-alertRow is-${a.severity || "info"}`}
                  onClick={() => (a?.link?.to ? navigate(a.link.to) : null)}
                >
                  <div className="dh-alertRow__main">
                    <div className="dh-alertRow__title">{a.title}</div>
                    <div className="dh-alertRow__meta">
                      {a.thresholdDays ? <span>Ngưỡng: {a.thresholdDays} ngày</span> : null}
                      {a.oldest?.updatedAt ? (
                        <span>
                          Cũ nhất: {new Date(a.oldest.updatedAt).toLocaleString("vi-VN")}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="dh-alertRow__count">{Number(a.count || 0)}</div>
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="dh-panel dh-panel--assets">
          <div className="dh-panel__head dh-panel__head--stack">
            <div className="dh-panel__title">Thiết bị (QR)</div>
            <div className="dh-panel__hint">Tóm tắt vòng đời tài sản để admin nhìn nhanh chất lượng vận hành</div>
          </div>

          <div className="dh-assetGrid">
            <div className="dh-assetKpi">
              <div className="dh-assetKpi__label">Đang hoạt động</div>
              <div className="dh-assetKpi__value">{Number(lifecycle.active || 0)}</div>
            </div>
            <div className="dh-assetKpi">
              <div className="dh-assetKpi__label">Đang bảo trì</div>
              <div className="dh-assetKpi__value">{Number(lifecycle.maintenance || 0)}</div>
            </div>
            <div className="dh-assetKpi">
              <div className="dh-assetKpi__label">Hỏng</div>
              <div className="dh-assetKpi__value">{Number(lifecycle.broken || 0)}</div>
            </div>
            <div className="dh-assetKpi">
              <div className="dh-assetKpi__label">Ngừng sử dụng</div>
              <div className="dh-assetKpi__value">{Number(lifecycle.retired || 0)}</div>
            </div>
            <div className="dh-assetKpi dh-assetKpi--warn" onClick={() => navigate("/admin/equipment-assets")} role="button" tabIndex={0}>
              <div className="dh-assetKpi__label">Thiếu QR</div>
              <div className="dh-assetKpi__value">{Number(assetKpis.missingQrCount || 0)}</div>
            </div>
          </div>

          <div className="dh-panel__actions">
            <button className="dh-btn dh-btn--ghost" onClick={() => navigate("/admin/equipment-assets")}>
              Mở thiết bị (QR)
            </button>
          </div>
        </section>

        <section className="dh-panel dh-panel--combos">
          <div className="dh-panel__head dh-panel__head--stack">
            <div className="dh-panel__title">Top 4 combo bán chạy</div>
            <div className="dh-panel__hint">Hiển thị 4 combo có số lượt bán cao nhất để admin theo dõi nhanh hiệu suất bán</div>
          </div>

          <div className="dh-combo-grid">
            {!sellingCombos.length ? (
              <div className="dh-empty">Chưa có combo đang bán.</div>
            ) : (
              sellingCombos.map((item) => {
                const imageUrl = absUrl(item.thumbnail);
                return (
                  <article className="dh-combo-card" key={item.id}>
                    <div className="dh-combo-card__media">
                      {imageUrl ? <img src={imageUrl} alt={item.name} /> : <span>{(item.name || "C").slice(0, 1).toUpperCase()}</span>}
                    </div>
                    <div className="dh-combo-card__body">
                      <div className="dh-combo-card__code">{item.code || `COMBO-${item.id}`}</div>
                      <div className="dh-combo-card__title">{item.name}</div>
                      <div className="dh-combo-card__desc">{shortText(item.description)}</div>
                      <div className="dh-combo-card__foot">
                        <span className="dh-combo-card__price">{fmtMoney(item.price)}</span>
                        <span className="dh-combo-card__time">{item.updatedAt ? new Date(item.updatedAt).toLocaleDateString("vi-VN") : "-"}</span>
                      </div>
                    </div>
                  </article>
                );
              })
            )}
          </div>
        </section>

        <section className="dh-panel dh-panel--chart">
          <div className="dh-panel__head dh-panel__head--stack">
            <div className="dh-panel__title">Doanh thu combo 30 ngày</div>
            <div className="dh-panel__hint">Xu hướng thanh toán hoàn tất theo giao dịch combo</div>
          </div>

          <div className="dh-chart">
            {!revenueSeries.length ? (
              <div className="dh-empty">Chưa có dữ liệu doanh thu.</div>
            ) : (
              <div className="dh-chartBox">
                <div className="dh-chartAxisY">
                  <span>{fmtMoney(maxRevenue)}</span>
                  <span>{fmtMoney(midRevenue)}</span>
                  <span>0đ</span>
                </div>
                <svg className="dh-chartSvg" viewBox="0 0 720 220" preserveAspectRatio="none" aria-label="Biểu đồ doanh thu 30 ngày">
                <defs>
                  <linearGradient id="dhRevenueFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(251,191,36,0.38)" />
                    <stop offset="100%" stopColor="rgba(251,191,36,0.03)" />
                  </linearGradient>
                </defs>
                <polyline className="dh-chartSvg__line" points={chartPoints} />
                <polygon className="dh-chartSvg__area" points={chartAreaPoints} fill="url(#dhRevenueFill)" />
                {chartLabelPoints.map((p) => (
                  <g key={p.idx}>
                    <circle cx={p.x} cy={p.y} r="4" className="dh-chartSvg__dot" />
                    <text x={p.x + 8} y={Math.max(14, p.y - 8)} className="dh-chartSvg__value">{fmtMoney(p.value)}</text>
                  </g>
                ))}
                </svg>
              </div>
            )}
          </div>

          <div className="dh-chartAxisX">
            <span>{firstDate}</span>
            <span>{middleDate}</span>
            <span>{lastDate}</span>
          </div>
          <div className="dh-chart-total">Tổng 30 ngày: {fmtMoney(data?.cards?.comboRevenue30d || 0)}</div>
        </section>
      </div>

      <section className="dh-panel dh-panel--table">
        <div className="dh-panel__head dh-panel__head--stack">
          <div className="dh-panel__title">Giao dịch combo gần nhất</div>
          <div className="dh-panel__hint">Kiểm tra nhanh dòng tiền theo từng giao dịch combo</div>
        </div>

        <div className="dh-table-wrap">
          <table className="dh-table">
            <thead>
              <tr>
                <th>Mã GD</th>
                <th>Gym</th>
                <th>Số tiền</th>
                <th>Phương thức</th>
                <th>Mô tả</th>
                <th>Ngày</th>
              </tr>
            </thead>
            <tbody>
              {!comboSalesTransactions.length ? (
                <tr>
                  <td colSpan={6} className="dh-table__empty">Chưa có giao dịch combo</td>
                </tr>
              ) : (
                comboSalesTransactions.map((tx) => (
                  <tr key={tx.id}>
                    <td>{tx.transactionCode || `TX-${tx.id}`}</td>
                    <td>{tx.gym?.name || "-"}</td>
                    <td>{fmtMoney(tx.amount)}</td>
                    <td>{tx.paymentMethod || "-"}</td>
                    <td>{tx.description || tx.purchaseRequest?.combo?.name || "-"}</td>
                    <td>{tx.transactionDate ? new Date(tx.transactionDate).toLocaleString("vi-VN") : "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
